// # migration reference: https://gitlab.com/giveinkind/inkind-react/-/blob/v20-27/src/app/views/blog/Blog.tsx

import type Article from "@gik/blog/models/Article";
import type ArticleType from "@gik/blog/models/ArticleType";
import type Author from "@gik/blog/models/Author";
import type Situation from "@gik/blog/models/Situation";
import { PageHeadKeys } from "@gik/core/hocs/PageHeadKeys";
import { createMetaTags, MetaTagsAssembler } from "@gik/core/hocs/withMeta";
import type WordpressYoastMeta from "@gik/core/models/wordpress/WordpressYoastMeta";
import { dateTimeFormatDetailed } from "@gik/l10n";
import he from "he";
import moment from "moment";
import React from "react";
import { stripHtml } from "string-strip-html";

const removeShortcodes = (text: string) => text.replace(/\[.*?\]/g, "");

function createYoastMetaTags(
	yoastMeta: WordpressYoastMeta,
	backupTitle = "",
	backupDescription = "",
) {
	return [
		<meta
			name="description"
			key={PageHeadKeys.META_DESCRIPTION}
			content={removeShortcodes(
				stripHtml(
					he.decode(yoastMeta?.yoast_wpseo_metadesc || backupDescription),
				).result,
			)}
		/>,
		<meta
			name="keywords"
			key={PageHeadKeys.META_KEYWORDS}
			content={yoastMeta?.yoast_wpseo_focuskw}
		/>,
		<meta
			property="og:title"
			key={PageHeadKeys.META_OG_TITLE}
			content={yoastMeta?.["yoast_wpseo_opengraph-title"] || backupTitle}
		/>,
		<meta
			property="og:description"
			key={PageHeadKeys.META_OG_DESCRIPTION}
			content={removeShortcodes(
				stripHtml(
					he.decode(
						yoastMeta?.["yoast_wpseo_opengraph-description"] ||
							backupDescription,
					),
				).result,
			)}
		/>,

		<meta
			property="og:image"
			key={PageHeadKeys.META_OG_IMAGE}
			content={yoastMeta?.["yoast_wpseo_opengraph-image"]}
		/>,
		<meta
			property="og:image:secure_url"
			key={PageHeadKeys.META_OG_IMAGE_SECURE_URL}
			content={yoastMeta?.["yoast_wpseo_opengraph-image"]}
		/>,
		<meta
			name="twitter:card"
			key={PageHeadKeys.META_TWITTER_CARD}
			content="summary"
		/>,
		<meta
			name="twitter:title"
			key={PageHeadKeys.META_TWITTER_TITLE}
			content={yoastMeta?.["yoast_wpseo_twitter-title"] || backupTitle}
		/>,
		<meta
			name="twitter:description"
			key={PageHeadKeys.META_TWITTER_DESCRIPTION}
			content={removeShortcodes(
				stripHtml(
					he.decode(
						yoastMeta?.["yoast_wpseo_twitter-description"] || backupDescription,
					),
				).result,
			)}
		/>,
		<meta
			name="twitter:image"
			key={PageHeadKeys.META_TWITTER_IMAGE}
			content={yoastMeta?.["yoast_wpseo_twitter-image"]}
		/>,
	];
}

export function withBlogArticlePageMetaTags(article: Article, component) {
	if (!article) {
		return component;
	}

	// yoast_wpseo_title already ends in " - Give InKind" and generateTitle()
	// appends it again -> strip a trailing suffix so exactly one remains.
	const pageTitle = (
		article.yoastMeta.yoast_wpseo_title ?? article.title
	).replace(/\s*-\s*Give InKind\s*$/i, "");
	const pageDescription =
		article.yoastMeta.yoast_wpseo_metadesc ?? article.excerpt;
	const pageImage = article.largeImageUrl;

	const tags = [
		...createMetaTags({
			type: "article",
			description: pageDescription,
			title: pageTitle,
			imageUrl: pageImage,
		}),
		<meta
			property="og:article:author"
			key={PageHeadKeys.META_OG_ARTICLE_AUTHOR}
			content={article.authorFullName}
		/>,
		<meta
			property="og:article:section"
			key={PageHeadKeys.META_OG_ARTICLE_SECTION}
			content={article.taxonomyItemName}
		/>,
		<meta
			property="og:article:published_time"
			key={PageHeadKeys.META_OG_ARTICLE_PUBLISH_TIME}
			content={moment(article.publishedDate).format(dateTimeFormatDetailed)}
		/>,
		<meta
			property="og:article:updated_time"
			key={PageHeadKeys.META_OG_ARTICLE_UPDATE_TIME}
			content={moment(article.modifiedDate).format(dateTimeFormatDetailed)}
		/>,
		...createYoastMetaTags(article.yoastMeta),
	];

	return <MetaTagsAssembler tags={tags}>{component}</MetaTagsAssembler>;
}

export function withArticleTypeMetaTags(articleType: ArticleType, component) {
	return (
		<MetaTagsAssembler
			tags={createYoastMetaTags(articleType.yoastMeta, articleType.name)}
		>
			{component}
		</MetaTagsAssembler>
	);
}

export function withAuthorMetaTags(author: Author, component) {
	return (
		<MetaTagsAssembler
			tags={createYoastMetaTags(
				author.yoastMeta,
				author.fullName,
				he.decode(author.bio).substring(0, 160),
			)}
		>
			{component}
		</MetaTagsAssembler>
	);
}

export function withSituationMetaTags(situation: Situation, component) {
	return (
		<MetaTagsAssembler
			tags={createYoastMetaTags(situation.yoastMeta, situation.name)}
		>
			{component}
		</MetaTagsAssembler>
	);
}
